import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, AlertCircle, ListChecks } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { humaniseSalesActionType } from "@/lib/salesActionLabel";

/**
 * DEV-019 · Sales Actions section for the Operations Project Detail.
 *
 * Manages the single ACTIVE Next Sales Action on a project plus a
 * concise history of superseded / completed rows. Reuses the
 * project's ``sales_actions[]`` sub-array so no parallel task store
 * exists.
 *
 * Authorisation mirrors the backend contract:
 *   * Operations (MANAGE_PROJECTS) — full CRUD.
 *   * Sales — full CRUD, but only for projects inside their
 *     SALES-ROLE-001B visibility scope (backend enforces + Detail
 *     page only mounts this card on visible projects anyway).
 *   * Customer / Installer — hidden entirely.
 */
const TYPE_LABEL_FALLBACK = {
    call_customer: "Call Customer",
    follow_up_quote: "Follow Up Quote",
    arrange_survey: "Arrange Survey",
    follow_up_survey: "Follow Up Survey",
    chase_decision: "Chase Decision",
    finance_followup: "Finance Follow-up",
    send_information: "Send Information",
    customer_meeting: "Customer Meeting",
    other: "Other",
};

function dueTone(dueDate) {
    if (!dueDate) return "neutral";
    const today = new Date().toISOString().slice(0, 10);
    const due = String(dueDate).slice(0, 10);
    if (due < today) return "overdue";
    if (due === today) return "today";
    return "upcoming";
}

function DueBadge({ dueDate }) {
    const tone = dueTone(dueDate);
    if (!dueDate) return <span className="text-[#4B5565] text-xs">No due date</span>;
    const cls = tone === "overdue"
        ? "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
        : tone === "today"
            ? "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]"
            : "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]";
    const label = tone === "overdue" ? "Overdue" : tone === "today" ? "Due today" : "Upcoming";
    return (
        <span
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${cls}`}
            data-testid={`ops-sales-action-due-${tone}`}
        >
            {tone === "overdue" ? <AlertCircle size={11} /> : <Clock size={11} />} {label} · {String(dueDate).slice(0, 10)}
        </span>
    );
}

export default function SalesActionsSection({ project, onProjectUpdated }) {
    const { user } = useAuth();
    const canManageProjects = useMemo(() => {
        if (!user) return false;
        if (Array.isArray(user.permissions) && user.permissions.length) {
            return user.permissions.includes("manage_projects");
        }
        return (user.role || "").toLowerCase() === "operations";
    }, [user]);
    const isSales = (user?.role || "").toLowerCase() === "sales";

    const [types, setTypes] = useState([]);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [form, setForm] = useState({
        action_type: "",
        note: "",
        due_date: "",
    });

    const active = project?.next_sales_action || null;
    const history = useMemo(
        () => (project?.sales_actions || []).filter((a) => a?.completed_at || a?.superseded_by)
            .sort((a, b) => (b?.completed_at || b?.superseded_at || "").localeCompare(a?.completed_at || a?.superseded_at || "")),
        [project?.sales_actions],
    );

    useEffect(() => {
        (async () => {
            try {
                const r = await api.listSalesActionTypes().catch(() => ({ types: [] }));
                setTypes(r?.types || []);
            } catch { /* non-fatal */ }
        })();
    }, []);

    useEffect(() => {
        setForm({
            action_type: active?.action_type || "",
            note: active?.note || "",
            due_date: (active?.due_date || "").slice(0, 10),
        });
        setEditing(false);
        setErr("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id, active?.id]);

    const typeLabel = useCallback((key) => {
        // DEV-021 · Prefer the backend catalogue label, then the local
        // fallback, then the shared presentation-side humaniser so a
        // never-mapped key still renders "Follow up survey" instead
        // of `follow_up_survey`.
        return (types.find((t) => t.key === key) || {}).label
            || TYPE_LABEL_FALLBACK[key]
            || humaniseSalesActionType(key)
            || key;
    }, [types]);

    // Hide entirely for non-authorised roles (customer / installer).
    // Kept AFTER hooks so React's rules-of-hooks ordering is preserved.
    if (!canManageProjects && !isSales) return null;

    const submit = async () => {
        setErr("");
        if (!form.action_type) {
            setErr("Please select an action type.");
            return;
        }
        if (form.action_type === "other" && !(form.note || "").trim()) {
            setErr("A short description is required when Action = Other.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                action_type: form.action_type,
                note: (form.note || "").trim() || null,
                due_date: form.due_date || null,
            };
            let updated;
            if (active && form.action_type === active.action_type) {
                updated = await api.updateSalesAction(project.id, active.id, payload);
            } else {
                updated = await api.createSalesAction(project.id, payload);
            }
            onProjectUpdated?.(updated);
            toast.success(active ? "Sales action updated" : "Next sales action set");
            setEditing(false);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const complete = async () => {
        if (!active) return;
        setSaving(true);
        try {
            const updated = await api.completeSalesAction(project.id, active.id);
            onProjectUpdated?.(updated);
            toast.success("Sales action marked complete");
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5"
            data-testid="ops-sales-actions"
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <span aria-hidden className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-[#EEF2FF] text-[#1E3A8A] shrink-0">
                        <ListChecks size={14} strokeWidth={1.75} />
                    </span>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                        Next Sales Action
                    </div>
                </div>
                {!editing && (
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        data-testid="ops-sales-actions-edit"
                        className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        {active ? "Change" : "Set next action"}
                    </button>
                )}
            </div>

            {!editing ? (
                active ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="ops-sales-actions-active">
                        <div>
                            <dt className="text-[10px] tracking-[0.14em] uppercase text-[#4B5565] font-medium">Action</dt>
                            <dd className="mt-1 text-[#131B2F]" data-testid="ops-sales-actions-type">
                                {typeLabel(active.action_type)}
                            </dd>
                            {active.note && (
                                <dd className="mt-1 text-xs text-[#4B5565]" data-testid="ops-sales-actions-note">
                                    {active.note}
                                </dd>
                            )}
                        </div>
                        <div>
                            <dt className="text-[10px] tracking-[0.14em] uppercase text-[#4B5565] font-medium">Due</dt>
                            <dd className="mt-1"><DueBadge dueDate={active.due_date} /></dd>
                            <button
                                type="button"
                                onClick={complete}
                                disabled={saving}
                                data-testid="ops-sales-actions-complete"
                                className="mt-3 inline-flex items-center gap-1.5 text-xs h-8 px-3 rounded-md bg-[#3E9E2E] text-white hover:bg-[#358D28] disabled:opacity-60"
                            >
                                <CheckCircle2 size={12} /> Mark complete
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="text-sm text-[#4B5565]" data-testid="ops-sales-actions-empty">
                        No next action set — click "Set next action" to record what happens next.
                    </div>
                )
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                        <label className="block text-xs text-[#4B5565] mb-1">Action type</label>
                        <select
                            data-testid="ops-sales-actions-type-select"
                            value={form.action_type}
                            onChange={(e) => setForm({ ...form, action_type: e.target.value })}
                            className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                        >
                            <option value="">Select…</option>
                            {(types.length
                                ? types
                                : Object.entries(TYPE_LABEL_FALLBACK).map(([key, label]) => ({ key, label }))
                            ).map((t) => (
                                <option key={t.key} value={t.key}
                                    data-testid={`ops-sales-actions-type-opt-${t.key}`}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-[#4B5565] mb-1">Due date</label>
                        <input
                            type="date"
                            data-testid="ops-sales-actions-due-input"
                            value={form.due_date}
                            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                            className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                        />
                    </div>
                    <div className="md:col-span-3">
                        <label className="block text-xs text-[#4B5565] mb-1">
                            Note{form.action_type === "other" ? " (required)" : " (optional)"}
                        </label>
                        <input
                            type="text"
                            data-testid="ops-sales-actions-note-input"
                            value={form.note}
                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                            maxLength={400}
                            placeholder="Short description…"
                            className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                        />
                    </div>
                    {!!err && (
                        <div className="md:col-span-3 text-xs text-[#DA3535]" data-testid="ops-sales-actions-error">
                            {err}
                        </div>
                    )}
                    <div className="md:col-span-3 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => { setEditing(false); setErr(""); }}
                            disabled={saving}
                            data-testid="ops-sales-actions-cancel"
                            className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7EB] text-[#131B2F]"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={saving}
                            data-testid="ops-sales-actions-save"
                            className="text-xs px-2.5 py-1 rounded-md bg-[#131B2F] text-white disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            )}

            {history.length > 0 && (
                <details className="mt-4" data-testid="ops-sales-actions-history">
                    <summary className="text-xs text-[#4B5565] cursor-pointer select-none">
                        History ({history.length})
                    </summary>
                    <ul className="mt-2 space-y-2">
                        {history.slice(0, 10).map((a) => (
                            <li
                                key={a.id}
                                className="text-xs text-[#4B5565] flex items-start gap-2"
                                data-testid={`ops-sales-actions-history-row-${a.id}`}
                            >
                                <span className={`inline-flex mt-0.5 w-2 h-2 rounded-full shrink-0 ${
                                    a.completed_at ? "bg-[#3E9E2E]" : "bg-[#B45309]"
                                }`} />
                                <span className="flex-1">
                                    <span className="text-[#131B2F] font-medium">{typeLabel(a.action_type)}</span>
                                    {a.note && <span> · {a.note}</span>}
                                    <span className="text-[#4B5565]">
                                        {" · "}
                                        {a.completed_at ? `Completed ${a.completed_at.slice(0, 10)}` : `Superseded ${(a.superseded_at || "").slice(0, 10)}`}
                                        {a.completed_by?.name ? ` by ${a.completed_by.name}` : ""}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </section>
    );
}
